import React, { FC } from 'react'
import { TitleController, Loading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { Alert, Illustration } from '@pandiafi/components'

import { GymBookingDataLoader } from './GymBookingDataLoader'
import { GymReservation } from './GymReservation'
import { getActiveGymRights } from './utils'

import { ExtraServiceReservationsList } from 'components/ExtraServiceReservations'
import {
  SidebarInfoSection,
  SidebarInfoSectionText,
  ReservationRulesAndTermsLinks,
  View,
} from 'components'
import { useAPIGetContractSuspense } from 'api/apiHooks/useAPIGetContract'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { useAPIGetRightsOfAccess } from 'api/apiHooks/useAPIGetRightsOfAccess'

export const GymBooking: FC = () => {
  const { t } = useTranslation(['common', 'titles', 'gymBooking'])
  const { frontPagePath } = useContractPaths()
  const { currentContractId } = useCurrentContract()
  const { data: contract } = useAPIGetContractSuspense(currentContractId)
  const { data: rightsOfAccess, isLoading: isRightsLoading } =
    useAPIGetRightsOfAccess(currentContractId)
  const moduleConfig = useModuleConfig()

  const gymModuleConfig = moduleConfig?.gym
  const gymFallbackLinks: { href: string; label: string }[] = []
  if (gymModuleConfig?.contractDocumentLink) {
    gymFallbackLinks.push({
      href: gymModuleConfig.contractDocumentLink,
      label: t('gymBooking:modal.gymRulesLink'),
    })
  }
  if (gymModuleConfig?.termsOfUseDocumentLink) {
    gymFallbackLinks.push({
      href: gymModuleConfig.termsOfUseDocumentLink,
      label: t('gymBooking:modal.gymAccessTermsLink'),
    })
  }

  const gymRights = getActiveGymRights(rightsOfAccess)
  const ownAccessText = isRightsLoading
    ? t('gymBooking:sidebar.ownAccessLoading')
    : gymRights.length > 0
      ? t('gymBooking:sidebar.ownAccessWithCount', {
          count: gymRights.length,
        })
      : t('gymBooking:sidebar.ownAccessNone')

  return (
    <TitleController title={t('titles:gymBooking')}>
      <View>
        <View.Header>
          <View.HeaderLink to={frontPagePath}>
            {t('common:views.home')}
          </View.HeaderLink>
          <View.HeaderTitle>{t('common:navbar.gymBooking')}</View.HeaderTitle>
        </View.Header>

        <View.Sidebar>
          <SidebarInfoSection className="space-y-6">
            <SidebarInfoSectionText>
              {t('gymBooking:sidebar.description')}
            </SidebarInfoSectionText>
            <SidebarInfoSectionText>{ownAccessText}</SidebarInfoSectionText>
            <ReservationRulesAndTermsLinks
              contractId={currentContractId}
              reservableCategory="gym"
              realPropertyHierarchyId={null}
              fallbackLinks={gymFallbackLinks}
            />
            <React.Suspense fallback={<Loading variant="details" />}>
              <ExtraServiceReservationsList type="right_of_access" />
            </React.Suspense>
          </SidebarInfoSection>
        </View.Sidebar>

        <View.Content>
          <div className="space-y-6 max-w-full">
            <GymBookingDataLoader>
              {(gymAreas, isLoading, userMetadata) => {
                if (isLoading) {
                  return (
                    <div className="flex justify-center items-center py-12">
                      <Loading variant="details" />
                    </div>
                  )
                }

                if (gymAreas.length === 0) {
                  return (
                    <Alert variant="info" className="my-8">
                      {t('gymBooking:noCalendarsAvailable')}
                    </Alert>
                  )
                }

                return (
                  <div className="space-y-8">
                    <div
                      aria-hidden="true"
                      className="flex justify-center pt-16"
                    >
                      <Illustration alt="" name="gym" />
                    </div>
                    <div className="divide-y divide-common-gray-5">
                      {gymAreas.map((gymArea) => (
                        <section
                          key={gymArea.id}
                          className="py-8 first:pt-0 last:pb-0"
                        >
                          <GymReservation
                            gymArea={gymArea}
                            tenantAccessInfo={contract.tenant}
                            rightsOfAccess={gymRights.filter(
                              (right) =>
                                right.realPropertyHierarchyId ===
                                gymArea.realPropertyHierarchyId
                            )}
                            readonly={userMetadata.isImpersonator}
                          />
                        </section>
                      ))}
                    </div>
                  </div>
                )
              }}
            </GymBookingDataLoader>
          </div>
        </View.Content>
      </View>
    </TitleController>
  )
}
